import React, { useState, useCallback } from 'react';
import {
  Modal,
  Upload,
  Button,
  Space,
  Typography,
  Checkbox,
  Radio,
  Alert,
  Divider,
  Spin,
  message,
  Tag,
  Card,
  Row,
  Col,
} from 'antd';
import {
  UploadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  SettingOutlined,
  TrophyOutlined,
  GiftOutlined,
  AimOutlined,
  StarOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { gamificationApi } from '../../../shared/api/gamificationApi';
import { ImportOptions, ImportPreview, ImportResult, GamificationExportData } from './types';

const { Text, Title, Paragraph } = Typography;

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ImportModal: React.FC<ImportModalProps> = ({ open, onClose, onSuccess }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [parsedData, setParsedData] = useState<GamificationExportData | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [step, setStep] = useState<'upload' | 'preview' | 'options'>('upload');

  const [options, setOptions] = useState<ImportOptions>({
    mode: 'merge',
    includeConfig: true,
    includeLevels: true,
    includeRules: true,
    includeObjectives: true,
    includeRedemptions: true,
  });

  const resetState = useCallback(() => {
    setFileList([]);
    setParsedData(null);
    setPreview(null);
    setStep('upload');
    setOptions({
      mode: 'merge',
      includeConfig: true,
      includeLevels: true,
      includeRules: true,
      includeObjectives: true,
      includeRedemptions: true,
    });
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleFileChange = useCallback(async (info: { fileList: UploadFile[] }) => {
    const newFileList = info.fileList.slice(-1); // Keep only last file
    setFileList(newFileList);

    if (newFileList.length === 0) {
      setParsedData(null);
      setPreview(null);
      setStep('upload');
      return;
    }

    const file = newFileList[0];
    if (file.originFileObj) {
      setLoading(true);
      try {
        const text = await file.originFileObj.text();
        const data = JSON.parse(text);
        setParsedData(data);

        // Call preview API
        const previewResult = await gamificationApi.previewImport(data);
        setPreview(previewResult);
        setStep('preview');
      } catch (e: any) {
        message.error('Error al leer el archivo: ' + (e.message || 'Formato invalido'));
        setParsedData(null);
        setPreview(null);
      } finally {
        setLoading(false);
      }
    }
  }, []);

  const handleImport = useCallback(async () => {
    if (!parsedData) return;

    setImporting(true);
    try {
      const result: ImportResult = await gamificationApi.importConfig(parsedData, options);

      if (result.success) {
        message.success(result.message);
        onSuccess();
        handleClose();
      } else {
        message.error(result.message);
        if (result.errors && result.errors.length > 0) {
          result.errors.forEach((err) => message.warning(err));
        }
      }
    } catch (e: any) {
      message.error('Error al importar: ' + (e.message || 'Error desconocido'));
    } finally {
      setImporting(false);
    }
  }, [parsedData, options, onSuccess, handleClose]);

  const renderUploadStep = () => (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
      <Upload.Dragger
        fileList={fileList}
        onChange={handleFileChange}
        beforeUpload={() => false}
        accept=".json"
        maxCount={1}
      >
        <p className="ant-upload-drag-icon">
          <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
        </p>
        <p className="ant-upload-text">
          Arrastra un archivo JSON o haz clic para seleccionar
        </p>
        <p className="ant-upload-hint">
          Solo archivos .json exportados desde este sistema
        </p>
      </Upload.Dragger>

      {loading && (
        <div style={{ marginTop: 20 }}>
          <Spin tip="Analizando archivo..." />
        </div>
      )}
    </div>
  );

  const renderPreviewStep = () => {
    if (!preview) return null;

    return (
      <div>
        {!preview.isValid && (
          <Alert
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            message="Archivo no valido"
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {preview.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            }
            style={{ marginBottom: 16 }}
          />
        )}

        {preview.warnings.length > 0 && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            message="Advertencias"
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {preview.warnings.map((warn, idx) => (
                  <li key={idx}>{warn}</li>
                ))}
              </ul>
            }
            style={{ marginBottom: 16 }}
          />
        )}

        {preview.isValid && (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message="Archivo valido"
            description="El archivo puede ser importado correctamente"
            style={{ marginBottom: 16 }}
          />
        )}

        <Card size="small" title="Contenido del archivo">
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <Space>
                <SettingOutlined style={{ color: '#13c2c2' }} />
                <Text>Configuracion:</Text>
                {preview.summary.configFound ? (
                  <Tag color="green">Incluida</Tag>
                ) : (
                  <Tag color="default">No incluida</Tag>
                )}
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <TrophyOutlined style={{ color: '#fa8c16' }} />
                <Text>Niveles:</Text>
                <Tag color="blue">{preview.summary.levelsCount}</Tag>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <GiftOutlined style={{ color: '#722ed1' }} />
                <Text>Reglas:</Text>
                <Tag color="purple">{preview.summary.rulesCount}</Tag>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <AimOutlined style={{ color: '#eb2f96' }} />
                <Text>Objetivos:</Text>
                <Tag color="magenta">{preview.summary.objectivesCount}</Tag>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <StarOutlined style={{ color: '#52c41a' }} />
                <Text>Canjes:</Text>
                <Tag color="green">{preview.summary.redemptionConfigsCount}</Tag>
              </Space>
            </Col>
          </Row>
        </Card>

        {parsedData && (
          <div style={{ marginTop: 16 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Exportado: {new Date(parsedData.exportedAt).toLocaleString()}
              {' | '}Version: {parsedData.version}
            </Text>
          </div>
        )}

        {preview.isValid && (
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <Button type="primary" onClick={() => setStep('options')}>
              Continuar con la importacion
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderOptionsStep = () => {
    if (!preview) return null;

    return (
      <div>
        <Title level={5} style={{ marginTop: 0 }}>Modo de importacion</Title>
        <Radio.Group
          value={options.mode}
          onChange={(e) => setOptions({ ...options, mode: e.target.value })}
        >
          <Space direction="vertical">
            <Radio value="merge">
              <Text strong>Agregar</Text>
              <Paragraph type="secondary" style={{ margin: 0, marginLeft: 24 }}>
                Agrega los elementos importados sin eliminar los existentes
              </Paragraph>
            </Radio>
            <Radio value="replace">
              <Text strong style={{ color: '#ff4d4f' }}>Reemplazar</Text>
              <Paragraph type="secondary" style={{ margin: 0, marginLeft: 24 }}>
                Elimina los elementos existentes antes de importar (segun las opciones seleccionadas)
              </Paragraph>
            </Radio>
          </Space>
        </Radio.Group>

        <Divider />

        <Title level={5}>Que importar</Title>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Checkbox
            checked={options.includeConfig}
            onChange={(e) => setOptions({ ...options, includeConfig: e.target.checked })}
            disabled={!preview.summary.configFound}
          >
            <Space>
              <SettingOutlined style={{ color: '#13c2c2' }} />
              <Text>Configuracion general</Text>
              {!preview.summary.configFound && <Tag color="default">No disponible</Tag>}
            </Space>
          </Checkbox>

          <Checkbox
            checked={options.includeLevels}
            onChange={(e) => setOptions({ ...options, includeLevels: e.target.checked })}
            disabled={preview.summary.levelsCount === 0}
          >
            <Space>
              <TrophyOutlined style={{ color: '#fa8c16' }} />
              <Text>Niveles ({preview.summary.levelsCount})</Text>
              {preview.summary.levelsCount === 0 && <Tag color="default">No disponible</Tag>}
            </Space>
          </Checkbox>

          <Checkbox
            checked={options.includeRules}
            onChange={(e) => setOptions({ ...options, includeRules: e.target.checked })}
            disabled={preview.summary.rulesCount === 0}
          >
            <Space>
              <GiftOutlined style={{ color: '#722ed1' }} />
              <Text>Reglas de bonus ({preview.summary.rulesCount})</Text>
              {preview.summary.rulesCount === 0 && <Tag color="default">No disponible</Tag>}
            </Space>
          </Checkbox>

          <Checkbox
            checked={options.includeObjectives}
            onChange={(e) => setOptions({ ...options, includeObjectives: e.target.checked })}
            disabled={preview.summary.objectivesCount === 0}
          >
            <Space>
              <AimOutlined style={{ color: '#eb2f96' }} />
              <Text>Objetivos de nivel ({preview.summary.objectivesCount})</Text>
              {preview.summary.objectivesCount === 0 && <Tag color="default">No disponible</Tag>}
            </Space>
          </Checkbox>

          <Checkbox
            checked={options.includeRedemptions}
            onChange={(e) => setOptions({ ...options, includeRedemptions: e.target.checked })}
            disabled={preview.summary.redemptionConfigsCount === 0}
          >
            <Space>
              <StarOutlined style={{ color: '#52c41a' }} />
              <Text>Configuraciones de canje ({preview.summary.redemptionConfigsCount})</Text>
              {preview.summary.redemptionConfigsCount === 0 && <Tag color="default">No disponible</Tag>}
            </Space>
          </Checkbox>
        </Space>

        {options.mode === 'replace' && (
          <Alert
            type="warning"
            showIcon
            message="Atencion"
            description="El modo 'Reemplazar' eliminara todos los elementos existentes de las categorias seleccionadas antes de importar los nuevos."
            style={{ marginTop: 16 }}
          />
        )}
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>Importar Gamificacion</span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      width={600}
      footer={
        step === 'options' ? (
          <Space>
            <Button onClick={() => setStep('preview')}>Atras</Button>
            <Button
              type="primary"
              onClick={handleImport}
              loading={importing}
              disabled={
                !options.includeConfig &&
                !options.includeLevels &&
                !options.includeRules &&
                !options.includeObjectives &&
                !options.includeRedemptions
              }
            >
              Importar
            </Button>
          </Space>
        ) : step === 'preview' ? (
          <Space>
            <Button onClick={() => { setStep('upload'); setFileList([]); setParsedData(null); setPreview(null); }}>
              Cambiar archivo
            </Button>
          </Space>
        ) : null
      }
    >
      {step === 'upload' && renderUploadStep()}
      {step === 'preview' && renderPreviewStep()}
      {step === 'options' && renderOptionsStep()}
    </Modal>
  );
};
